/**
 * The electromagnetic spectrum: conversions between wavelength, frequency, photon energy
 * and wavenumber, and the name of the band a value falls in.
 *
 * c, h, e and N_A are exact by definition of the SI (since 2019), so every conversion
 * is exact to floating-point precision. Wavelengths are in vacuum.
 */

/** Speed of light in vacuum, m/s (exact). */
export const SPEED_OF_LIGHT = 299_792_458;
/** Planck constant, J·s (exact). */
export const PLANCK = 6.626_070_15e-34;
/** Elementary charge, C — and so the joules in one electronvolt (exact). */
export const ELEMENTARY_CHARGE = 1.602_176_634e-19;
/** Avogadro constant, 1/mol (exact). */
export const AVOGADRO = 6.022_140_76e23;

/** hc in eV·nm: 1239.84198… */
export const HC_EV_NM = (PLANCK * SPEED_OF_LIGHT) / ELEMENTARY_CHARGE / 1e-9;

export interface PhotonValues {
  /** Wavelength in vacuum, m. */
  wavelength: number;
  /** Frequency, Hz. */
  frequency: number;
  /** Photon energy, J. */
  joules: number;
  /** Photon energy, eV. */
  electronvolts: number;
  /** Energy of one mole of such photons, kJ/mol. */
  kilojoulesPerMole: number;
  /** Wavenumber 1/λ, in m⁻¹. */
  wavenumber: number;
}

export type PhotonInput = "wavelength" | "frequency" | "energy" | "wavenumber";

function fromWavelength(wavelength: number): PhotonValues {
  const frequency = SPEED_OF_LIGHT / wavelength;
  const joules = PLANCK * frequency;
  return {
    wavelength,
    frequency,
    joules,
    electronvolts: joules / ELEMENTARY_CHARGE,
    kilojoulesPerMole: (joules * AVOGADRO) / 1000,
    wavenumber: 1 / wavelength,
  };
}

/**
 * Every spectral quantity from one. `value` is in SI base units for the input kind:
 * metres, hertz, joules or reciprocal metres. Null unless the value is above zero.
 */
export function photonFrom(
  kind: PhotonInput,
  value: number
): PhotonValues | null {
  if (!(Number.isFinite(value) && value > 0)) {
    return null;
  }
  if (kind === "wavelength") {
    return fromWavelength(value);
  }
  if (kind === "frequency") {
    return fromWavelength(SPEED_OF_LIGHT / value);
  }
  if (kind === "energy") {
    return fromWavelength((PLANCK * SPEED_OF_LIGHT) / value);
  }
  return fromWavelength(1 / value);
}

export type BandId =
  | "gamma"
  | "xray"
  | "ultraviolet"
  | "visible"
  | "infrared"
  | "microwave"
  | "radio";

export type SubBandId =
  | "uvc"
  | "uvb"
  | "uva"
  | "violet"
  | "blue"
  | "green"
  | "yellow"
  | "orange"
  | "red"
  | "nearIr"
  | "midIr"
  | "farIr";

/** Band edges in metres, short to long. */
export const BAND_EDGES = {
  gammaXray: 1e-11,
  xrayUv: 1e-8,
  uvVisible: 380e-9,
  visibleIr: 750e-9,
  irMicrowave: SPEED_OF_LIGHT / 300e9,
  microwaveRadio: SPEED_OF_LIGHT / 300e6,
} as const;

/** Sub-band edges inside ultraviolet, visible and infrared, in metres. */
const UV_SUB: [number, SubBandId][] = [
  [280e-9, "uvc"],
  [315e-9, "uvb"],
  [Number.POSITIVE_INFINITY, "uva"],
];
const VISIBLE_SUB: [number, SubBandId][] = [
  [450e-9, "violet"],
  [495e-9, "blue"],
  [570e-9, "green"],
  [590e-9, "yellow"],
  [620e-9, "orange"],
  [Number.POSITIVE_INFINITY, "red"],
];
const IR_SUB: [number, SubBandId][] = [
  [3e-6, "nearIr"],
  [50e-6, "midIr"],
  [Number.POSITIVE_INFINITY, "farIr"],
];

function pick(edges: [number, SubBandId][], wavelength: number): SubBandId {
  return (edges.find(([limit]) => wavelength < limit) ?? edges[0])[1];
}

export interface BandInfo {
  band: BandId;
  sub: SubBandId | null;
  /** ITU radio band acronym (ELF … EHF) for radio and microwave frequencies. */
  itu: string | null;
}

/** ITU radio bands by lower frequency edge in Hz, high to low. */
const ITU_BANDS: [number, string][] = [
  [30e9, "EHF"],
  [3e9, "SHF"],
  [300e6, "UHF"],
  [30e6, "VHF"],
  [3e6, "HF"],
  [300e3, "MF"],
  [30e3, "LF"],
  [3e3, "VLF"],
  [300, "ULF"],
  [30, "SLF"],
  [3, "ELF"],
];

function ituBand(frequency: number): string | null {
  return ITU_BANDS.find(([lower]) => frequency >= lower)?.[1] ?? null;
}

/** Which band a vacuum wavelength falls in, with its finer subdivision where there is one. */
export function classify(wavelength: number): BandInfo {
  const none = { sub: null, itu: null } as const;
  if (wavelength < BAND_EDGES.gammaXray) {
    return { band: "gamma", ...none };
  }
  if (wavelength < BAND_EDGES.xrayUv) {
    return { band: "xray", ...none };
  }
  if (wavelength < BAND_EDGES.uvVisible) {
    return { band: "ultraviolet", sub: pick(UV_SUB, wavelength), itu: null };
  }
  if (wavelength < BAND_EDGES.visibleIr) {
    return { band: "visible", sub: pick(VISIBLE_SUB, wavelength), itu: null };
  }
  if (wavelength < BAND_EDGES.irMicrowave) {
    return { band: "infrared", sub: pick(IR_SUB, wavelength), itu: null };
  }
  const itu = ituBand(SPEED_OF_LIGHT / wavelength);
  const band = wavelength < BAND_EDGES.microwaveRadio ? "microwave" : "radio";
  return { band, sub: null, itu };
}

/**
 * An on-screen approximation of the colour of a visible wavelength (Dan Bruton's
 * piecewise fit, with the usual fade at the edges of vision). Not a colorimetric
 * match; null outside 380–750 nm.
 */
export function wavelengthToRgb(nanometres: number): string | null {
  if (nanometres < 380 || nanometres > 750) {
    return null;
  }
  const w = nanometres;
  let r = 0;
  let g = 0;
  let b = 0;
  if (w < 440) {
    r = (440 - w) / 60;
    b = 1;
  } else if (w < 490) {
    g = (w - 440) / 50;
    b = 1;
  } else if (w < 510) {
    g = 1;
    b = (510 - w) / 20;
  } else if (w < 580) {
    r = (w - 510) / 70;
    g = 1;
  } else if (w < 645) {
    r = 1;
    g = (645 - w) / 65;
  } else {
    r = 1;
  }
  let fade = 1;
  if (w < 420) {
    fade = 0.3 + (0.7 * (w - 380)) / 40;
  } else if (w > 700) {
    fade = 0.3 + (0.7 * (750 - w)) / 50;
  }
  const channel = (x: number) => Math.round(255 * (x * fade) ** 0.8);
  return `rgb(${channel(r)}, ${channel(g)}, ${channel(b)})`;
}

/** The decades the spectrum bar spans, as log10 of the wavelength in metres. */
export const SPECTRUM_LOG_RANGE: readonly [number, number] = [-13, 4];

/** Position 0–1 along the spectrum bar for a wavelength, clamped to the ends. */
export function spectrumPosition(wavelength: number): number {
  const [low, high] = SPECTRUM_LOG_RANGE;
  const position = (Math.log10(wavelength) - low) / (high - low);
  return Math.min(1, Math.max(0, position));
}
